import React, { useState, useEffect } from "react";
// import ProgressBar from "./ProgressBar";
import { formatTimeDifference } from "../utils/index";
import { 
  checkUserVoteStatus, 
  checkVotingPower,
  evaluateProposalResult 
} from "../config/snapshot";
import { Link } from "react-router-dom";
import { finalizeVoteResult } from "../config/web3";
import { ENV } from "../config/config";
import { Alert } from "react-bootstrap";
import { useAccount } from 'wagmi';

const GovernanceCard = ({ proposal, type, fetchProposalsFn }) => {
  const { choices = [], end, id, scores = [], start, state, title, body } = proposal;
  const { address, isConnected } = useAccount();
  
  const [userVoteStatus, setUserVoteStatus] = useState({ hasVoted: false, choice: null });
  const [votingPower, setVotingPower] = useState({ hasVotingPower: false, votingPower: 0 });
  const [statusLoading, setStatusLoading] = useState(false);

  // Check user status when component mounts or address changes
  useEffect(() => {
    if (isConnected && address && id) {
      checkUserStatus();
    }
  }, [isConnected, address, id]);

  const checkUserStatus = async () => {
    if (!address) return;
    
    try {
      setStatusLoading(true);
      
      // Check if user has already voted
      const voteStatus = await checkUserVoteStatus(id, address);
      setUserVoteStatus(voteStatus);

      // Check user's voting power (only if they haven't voted and voting is active)
      if (!voteStatus.hasVoted && Date.now() / 1000 <= end && Date.now() / 1000 >= start) {
        const powerStatus = await checkVotingPower(id, address, proposal.snapshot);
        setVotingPower(powerStatus);
      }
    } catch (error) {
      console.error("Error checking user status:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  const finalizeVoteResultFn = async () => {
    try {
      await finalizeVoteResult(id, scores, ENV.dstChainID1);
      fetchProposalsFn();
    } catch (err) {
      console.error("Finalize error:", err);
    }
  }

  const totalVotes = scores.reduce((a, b) => a + b, 0);
  const quorum = ENV.quorum;
  const progress = (totalVotes / quorum) * 100;
  const isVotingActive = Date.now() / 1000 <= end && Date.now() / 1000 >= start;
  const hasVotingEnded = Date.now() / 1000 > end;
  
  // Get the result status for ended proposals
  const resultStatus = hasVotingEnded ? evaluateProposalResult(proposal, quorum, ENV.vetoThreshold) : null;

  // Function to get user voting status message
  const getUserStatusMessage = () => {
    if (!isConnected || !address) {
      return null; // Don't show any status if not connected
    }

    if (statusLoading) {
      return (
        <Alert variant="info" className="mb-2 py-2">
          <small>Checking voting status...</small>
        </Alert>
      );
    }

    if (userVoteStatus.hasVoted) {
      const choiceName = choices[userVoteStatus.choice - 1]; // Snapshot uses 1-based indexing
      return (
        <Alert variant="success" className="mb-2 py-2">
          <small><strong>✓ You voted:</strong> "{choiceName}"</small>
        </Alert>
      );
    }

    if (isVotingActive && !votingPower.hasVotingPower) {
      return (
        <Alert variant="warning" className="mb-2 py-2">
          <small><strong>⚠ Insufficient voting power</strong> - You cannot vote on this proposal</small>
        </Alert>
      );
    }

    if (isVotingActive && votingPower.hasVotingPower) {
      return (
        <Alert variant="info" className="mb-2 py-2">
          <small><strong>You can vote</strong> - Click "Go to Details" to cast your vote</small>
        </Alert>
      );
    }

    return null;
  };

  return (
    <div className="p-2 col-md-6">
      <div className="card-component p-3">
        <div className="flex-between">
          <div className="d-flex align-items-center gap-2">
            <div className="card-icon">
              <img src="/images/coinmarket.svg" alt="card icon" />
            </div>
            <div className="fw-bold">{title}</div>
          </div>
          <div className="text-primary">
            <Link to={`/voting/${id}`} className="btn btn-outline-primary btn-sm">
              Go to Details
            </Link>
          </div>
        </div>
        
        <h5 className="mt-3">{body}</h5>
        <p className="text-primary mb-2">Submitted {formatTimeDifference(start)}</p>

        {/* Show voting status */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <span className="badge bg-secondary">
              {state === 'active' ? 'Active' : state === 'closed' ? 'Ended' : 'Pending'}
            </span>
            {hasVotingEnded && resultStatus && (
              <span className={`badge ${
                resultStatus === 'Approved' ? 'bg-success' : 
                resultStatus.includes('Rejected') ? 'bg-danger' : 'bg-secondary'
              }`}>
                {resultStatus}
              </span>
            )}
          </div>
        </div>

        {/* User voting status message */}
        {getUserStatusMessage()}

        <div className="my-3">
          {/* <ProgressBar progress={progress} quorum={100} /> */}
          <div className="mt-2">
            <small>
              <strong>Quorum Required:</strong> {quorum.toLocaleString()} | 
              <strong> Achieved:</strong> {totalVotes.toLocaleString()} ({progress.toFixed(1)}%)
            </small>
          </div>
        </div>

        <div className="mb-3">
          <p className="mb-2 fw-bold">
            {hasVotingEnded 
              ? "Voting Ended" 
              : `Ends in ${formatTimeDifference(end)}`
            }
          </p>
          
          {/* Show current vote distribution */}
          {choices.length > 0 && (
            <div className="row g-2">
              {choices.map((choice, index) => {
                const voteCount = scores[index] || 0;
                const percentage = totalVotes > 0 ? ((voteCount / totalVotes) * 100).toFixed(1) : "0.0";
                
                return (
                  <div key={index} className="col-6">
                    <div className="card-component p-2 text-center">
                      <div className="fw-bold text-truncate" title={choice}>{choice}</div>
                      <div className="text-primary">{percentage}%</div>
                      <small>{voteCount.toLocaleString()} votes</small>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Voting Instructions */}
        {isVotingActive && isConnected && address && (
          <Alert variant="light" className="mb-3 py-2">
            <small>
              <strong>💡 To vote:</strong> Click "Go to Details" above to view full proposal and cast your vote securely.
            </small>
          </Alert>
        )}

        {/* Finalize button for admins */}
        {type === "finalize" && (
          <div className="mt-3">
            <button
              onClick={() => finalizeVoteResultFn()}
              className="btn btn-danger btn-sm"
            >
              Finalize Vote Result
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovernanceCard;